import {useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Snackbar, Stack,
    TextField
} from '@mui/material';
import {MuiTelInput} from 'mui-tel-input';
import {FuncButtonWIcon} from '../../../common/buttons/FunctionalButton';
import {getStyles} from '../../customerCommon';
import {ICustomerGuid} from '@mybake/ui/models/Customers/ICustomerGuid';
import {ICustomerWithInfos} from '../../../../models/Customers/ICustomerWithInfos';
import {compareObjects} from '@mybake/ui/common/helpers';
import {CustomerValidator} from '../../../../validators/CustomerValidator';
import {saveCustomer} from '../../dataManager';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {getApiErrorKind, getApiErrorMessage, getApiFieldErrors, getApiTraceId} from '../../../../common/apiErrors';
import TraceIdFeedback from '../../../feedback/TraceIdFeedback';

interface Props {
    item: ICustomerGuid | ICustomerWithInfos;
    cancelEdit: (id: string | number) => void;
}

export const UpdateForm = ({item, cancelEdit}: Props) => {
    const {t} = useAppTranslation();
    const {opacityHalf, progressLoader} = getStyles();
    const {save, saveCustomerError, saveCustomerIsSuccess} = saveCustomer();
    const [isLoading, setIsLoading] = useState(false);
    const [modified, setModified] = useState(false);
    const [isButtonDisable, setIsButtonDisable] = useState(true);
    const [updatedData, setUpdatedData] = useState<ICustomerGuid | ICustomerWithInfos>({...item});
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState<'error' | 'success' | undefined>();
    const [isValidEmail, setIsValidEmail] = useState<'error' | 'success' | undefined>();
    const [isValidFirstName, setIsValidFirstName] = useState<'error' | 'success' | undefined>();
    const [isValidLastName, setIsValidLastName] = useState<'error' | 'success' | undefined>();
    const [isValidInstagram, setIsValidInstagram] = useState<'error' | 'success' | undefined>();
    const [isValidFacebook, setIsValidFacebook] = useState<'error' | 'success' | undefined>();
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [requestError, setRequestError] = useState('');
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');
    const inputFirstNameRef = useRef<HTMLInputElement>(null);
    const validator = useMemo(() => CustomerValidator.getInstance(updatedData), [updatedData]);
    const isNameMissing = !updatedData.firstName?.trim();

    const pulseStyle = {
        '& .MuiOutlinedInput-root': {
            animation: 'customerNamePulse 2s infinite',
        },
        '@keyframes customerNamePulse': {
            '0%, 100%': {boxShadow: '0 0 5px #b58c3f'},
            '50%': {boxShadow: '0 0 15px #f54100'},
        },
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {id, value} = event.target;
        setFieldErrors((previous) => ({...previous, [id]: '', name: ''}));
        setUpdatedData((previous) => ({...previous, [id]: value}));

        if (id === 'email') {
            const isValid = validator.isValidEmail(value, true);
            setIsValidEmail(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        }
        if (id === 'firstName') {
            const isValid = validator.isValidFirstName(value, true);
            setIsValidFirstName(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        }
        if (id === 'lastName') {
            const isValid = validator.isValidLastName(value, true);
            setIsValidLastName(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        }
        if (id === 'instagram') {
            const isValid = validator.isValidInstagram(value, true);
            setIsValidInstagram(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        }
        if (id === 'faceBook') {
            const isValid = validator.isValidFacebook(value, true);
            setIsValidFacebook(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        }
        setModified(true);
    };

    const handlePhoneNumber = (phoneNumber: string) => {
        setFieldErrors((previous) => ({...previous, phone: '', phoneNumber: '', contact: ''}));
        const isValid = validator.isValidPhoneNumber(phoneNumber, true);
        setIsValidPhoneNumber(isValid === undefined ? undefined : isValid ? 'success' : 'error');
        setUpdatedData((previous) => ({...previous, phoneNumber}));
        setModified(true);
    };

    const handleSave = () => {
        if (typeof updatedData.id !== 'string') return;
        setIsLoading(true);
        save({id: updatedData.id, customer: updatedData as ICustomerGuid});
    };

    useEffect(() => {
        if (!saveCustomerError) return;
        setIsLoading(false);
        const errors = getApiFieldErrors(saveCustomerError, t);
        setFieldErrors(errors);
        if (getApiErrorKind(saveCustomerError) === 'server') {
            setServerToast(getApiErrorMessage(saveCustomerError, t));
            setTraceId(getApiTraceId(saveCustomerError));
            setRequestError('');
        } else {
            setRequestError(Object.keys(errors).length ? '' : getApiErrorMessage(saveCustomerError, t));
        }
    }, [saveCustomerError, t]);

    useEffect(() => {
        if (saveCustomerIsSuccess) {
            setIsLoading(false);
            if (updatedData.id) cancelEdit(updatedData.id);
        }
    }, [saveCustomerIsSuccess]);

    useEffect(() => {
        if (!modified) return;
        const isUnchanged = compareObjects(item, updatedData);
        setModified(false);
        setIsButtonDisable(!(validator.validate() && !isUnchanged));
    }, [updatedData]);

    useEffect(() => {
        if (isNameMissing) inputFirstNameRef.current?.focus();
    }, [isNameMissing]);

    return (
        <Card sx={{mt: 2, position: 'relative'}}>
            {isLoading && (
                <Box sx={progressLoader}>
                    <CircularProgress color="success"/>
                </Box>
            )}
            <CardContent>
                <Stack spacing={3} sx={isLoading ? opacityHalf : {}}>
                    {requestError && <Alert severity="error">{requestError}</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                inputRef={inputFirstNameRef}
                                autoFocus={isNameMissing}
                                id="firstName"
                                label={t('customers:first_name')}
                                fullWidth
                                focused={isValidFirstName !== undefined}
                                color={isValidFirstName}
                                variant="outlined"
                                value={updatedData.firstName ?? ''}
                                onChange={handleChange}
                                sx={isNameMissing ? pulseStyle : undefined}
                                error={Boolean(fieldErrors.firstName || fieldErrors.name)}
                                helperText={fieldErrors.firstName || fieldErrors.name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField id="lastName" label={t('customers:last_name')} fullWidth focused={isValidLastName !== undefined}
                                       color={isValidLastName} variant="outlined" value={updatedData.lastName ?? ''} onChange={handleChange}
                                       error={Boolean(fieldErrors.lastName)} helperText={fieldErrors.lastName}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField id="email" label={t('customers:email')} fullWidth focused={isValidEmail !== undefined}
                                       color={isValidEmail} variant="outlined" value={updatedData.email ?? ''} onChange={handleChange}
                                       error={Boolean(fieldErrors.email || fieldErrors.contact)} helperText={fieldErrors.email || fieldErrors.contact}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <MuiTelInput id="phone" label={t('customers:phone_number')} placeholder={t('customers:phone_number')}
                                         fullWidth focused={isValidPhoneNumber !== undefined} color={isValidPhoneNumber} variant="outlined"
                                         value={updatedData.phoneNumber ?? ''} onChange={handlePhoneNumber} defaultCountry="PL"
                                         onlyCountries={['UA', 'PL', 'DE', 'IE']} forceCallingCode
                                         error={Boolean(fieldErrors.phone || fieldErrors.phoneNumber || fieldErrors.contact)}
                                         helperText={fieldErrors.phone || fieldErrors.phoneNumber || fieldErrors.contact}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField id="instagram" label={t('customers:instagram')} fullWidth focused={isValidInstagram !== undefined}
                                       color={isValidInstagram} variant="outlined" value={updatedData.instagram ?? ''} onChange={handleChange}
                                       error={Boolean(fieldErrors.instagram || fieldErrors.contact)} helperText={fieldErrors.instagram || fieldErrors.contact}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField id="faceBook" label={t('customers:facebook')} fullWidth focused={isValidFacebook !== undefined}
                                       color={isValidFacebook} variant="outlined" value={updatedData.faceBook ?? ''} onChange={handleChange}
                                       error={Boolean(fieldErrors.faceBook || fieldErrors.facebook || fieldErrors.contact)} helperText={fieldErrors.faceBook || fieldErrors.facebook || fieldErrors.contact}/>
                        </Grid>
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end">
                        <FuncButtonWIcon disabled={isButtonDisable} field={t('common:save')} handleClick={handleSave} iconName="sendIcon"/>
                    </Stack>
                </Stack>
            </CardContent>
            <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}>
                <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert>
            </Snackbar>
        </Card>
    );
};
